import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Space, Spin, Table, Tabs, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EntityCardOverlayFrame } from '../../components/entityCard/EntityCardOverlayFrame';
import { useReferenceCardOverlay } from '../../hooks/useReferenceCardOverlay';
import type { ColumnsType } from 'antd/es/table';
import { ApplicationCell, ExecutionCell, ExternalApplicationCell, PageLayout, StatusCell } from '../../ui';
import { EMPTY_FIELD, emptyListMessage } from '../../ui/entityCard/entityCardLabels';
import useHasPermission from '../../hooks/useHasPermission';
import { useGetDriverByIdQuery } from '../../store/api/referencesApi';
import { useGetAllRoutesQuery } from '../../store/api/routesApi';
import CreateDriverModal from '../../components/common/CreateDriverModal';
import EntityDocumentsBlock from '../../components/documents/EntityDocumentsBlock';
import { vehicleCategoryLabel } from '../../constants/vehicleCategory';
import type { Application, Route } from '../../types';
import { formatDate } from '../../utils/applicationDisplayFormat';
import {
  ENTITY_CARD_DASH,
  entityCardRouteDirectionLabel,
  entityCardRouteLoadingDate,
  entityCardRouteUnloadingDate,
  primaryClientForRoute
} from '../../utils/entityCardRouteUtils';
import '../contractors/ContractorCardPage.css';
import './DriverCardPage.css';

const { Text } = Typography;

const cardSurface: React.CSSProperties = {
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: 8
};

const sectionTitle = (t: string) => <Typography.Title level={5} style={{ margin: 0 }}>{t}</Typography.Title>;

const DriverCardPageContent: React.FC = () => {
  const navigate = useNavigate();
  const { isOverlay, close } = useReferenceCardOverlay();
  const { id: idParam } = useParams<{ id: string }>();
  const idNum = idParam ? parseInt(idParam, 10) : NaN;
  const skip = Number.isNaN(idNum);

  const canViewReferences = useHasPermission('references.view');
  const canEditReferences = useHasPermission('references.edit');
  const canCreateReferences = useHasPermission('references.create');
  const canModifyDriver = canEditReferences || canCreateReferences;

  const { data, isLoading, isError, error, refetch: refetchDriver } = useGetDriverByIdQuery(idNum, {
    skip: skip || !canViewReferences
  });

  const effectiveDriverId = !skip && Number.isFinite(idNum) && idNum > 0 ? idNum : null;

  const routesQueryArg =
    effectiveDriverId != null ? { driverId: effectiveDriverId, page: 1, limit: 30 } : { page: 1, limit: 30 };

  const { data: routesData, isLoading: routesLoading, isError: routesError } = useGetAllRoutesQuery(routesQueryArg, {
    skip: effectiveDriverId == null || !canViewReferences
  });

  const [editOpen, setEditOpen] = useState(false);

  const routes = useMemo(() => {
    const list = routesData?.routes ?? [];
    if (effectiveDriverId == null) return list;
    return list.filter((r) => Number(r.driverId) === effectiveDriverId);
  }, [routesData?.routes, effectiveDriverId]);

  const uniqueApplications: Application[] = useMemo(() => {
    const byId = new Map<number, Application>();
    for (const r of routes) {
      for (const ra of r.applications ?? []) {
        const app = ra?.application;
        if (app?.id && !byId.has(app.id)) byId.set(app.id, app);
      }
    }
    return Array.from(byId.values()).sort((a, b) => b.id - a.id);
  }, [routes]);

  const headerSubtitle = data ? (
    <Space direction="vertical" size={2}>
      <Text type="secondary">{data.phone?.trim() || 'телефон не указан'}</Text>
      {data.carrier ? (
        <Text type="secondary">
          Перевозчик:{' '}
          <Link to={`/carriers/${data.carrier.id}`} onClick={(e) => e.stopPropagation()}>
            {data.carrier.name}
          </Link>
        </Text>
      ) : (
        <Text type="secondary">Перевозчик не привязан</Text>
      )}
    </Space>
  ) : null;

  const routeColumns: ColumnsType<Route> = useMemo(
    () => [
      {
        title: '№ маршрута',
        key: 'num',
        width: 130,
        render: (_: unknown, r: Route) => {
          const text = r.number?.trim() ? r.number : String(r.id);
          return (
            <Link to={`/routes/${r.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
              {text}
            </Link>
          );
        }
      },
      {
        title: 'Маршрут',
        key: 'dir',
        ellipsis: true,
        render: (_: unknown, r: Route) => entityCardRouteDirectionLabel(r)
      },
      {
        title: 'Клиент',
        key: 'client',
        width: 200,
        ellipsis: true,
        render: (_: unknown, r: Route) => {
          const c = primaryClientForRoute(r);
          if (!c) return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
          return (
            <Link to={`/clients/${c.id}`} onClick={(e) => e.stopPropagation()}>
              {c.label}
            </Link>
          );
        }
      },
      {
        title: 'Исполнение',
        key: 'execution',
        width: 200,
        ellipsis: true,
        render: (_: unknown, r: Route) => <ExecutionCell route={r} />
      },
      {
        title: 'Погрузка',
        key: 'loadDt',
        width: 130,
        align: 'center',
        render: (_: unknown, r: Route) => entityCardRouteLoadingDate(r)
      },
      {
        title: 'Выгрузка',
        key: 'unloadDt',
        width: 130,
        align: 'center',
        render: (_: unknown, r: Route) => entityCardRouteUnloadingDate(r)
      },
      {
        title: 'Статус',
        key: 'st',
        width: 150,
        render: (_: unknown, r: Route) => <StatusCell status={String(r.status ?? '')} />
      },
      {
        title: 'Штрафы',
        key: 'pen',
        width: 160,
        align: 'right',
        render: () => <Text type="secondary">{ENTITY_CARD_DASH}</Text>
      }
    ],
    []
  );

  const applicationColumns: ColumnsType<Application> = useMemo(
    () => [
      {
        title: 'Заявка',
        key: 'app',
        width: 200,
        render: (_: unknown, a: Application) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ApplicationCell application={a} />
          </div>
        )
      },
      {
        title: 'Клиент',
        key: 'cl',
        width: 200,
        ellipsis: true,
        render: (_: unknown, a: Application) => {
          const id = a.clientId;
          const name = a.client?.name?.trim() || ENTITY_CARD_DASH;
          if (id != null) {
            return (
              <Link to={`/clients/${id}`} onClick={(e) => e.stopPropagation()}>
                {name}
              </Link>
            );
          }
          return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
        }
      },
      {
        title: 'Внешний №',
        key: 'ext',
        width: 200,
        render: (_: unknown, a: Application) => <ExternalApplicationCell application={a} />
      },
      {
        title: 'Погрузка',
        key: 'pl',
        width: 120,
        render: (_: unknown, a: Application) => <span>{formatDate(a.plannedLoadingDate)}</span>
      },
      {
        title: 'Выгрузка',
        key: 'unl',
        width: 120,
        render: (_: unknown, a: Application) => <span>{formatDate(a.plannedUnloadingDate)}</span>
      },
      {
        title: 'Статус',
        key: 'st',
        width: 130,
        render: (_: unknown, a: Application) => <StatusCell status={String(a.status ?? '')} />
      }
    ],
    []
  );

  if (!canViewReferences) {
    return (
      <PageLayout title="Водитель">
        <Card>
          <Text>У вас нет прав для просмотра справочников.</Text>
        </Card>
      </PageLayout>
    );
  }

  if (skip) {
    return (
      <PageLayout title="Водитель">
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout title="Водитель" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    const msg =
      (error as { data?: { message?: string; error?: string } })?.data?.message ||
      (error as { data?: { error?: string } })?.data?.error ||
      'Водитель не найден или недоступен.';
    return (
      <PageLayout title="Водитель">
        <Alert type="error" message={msg} showIcon />
      </PageLayout>
    );
  }

  const truck = data.defaultVehicle;
  const trailer = data.defaultTrailerVehicle;

  return (
    <div className="contractor-card-page driver-card-page">
      <PageLayout
        titleLevel={2}
        title={data.fullName}
        subtitle={headerSubtitle}
        actions={
          isOverlay || canModifyDriver ? (
            <Space>
              {isOverlay && (
                <Button type="default" onClick={close}>
                  Закрыть
                </Button>
              )}
              {canModifyDriver ? (
                <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                  Редактировать
                </Button>
              ) : null}
            </Space>
          ) : undefined
        }
      >
        <div className="contractor-card-grid">
          <Card title={sectionTitle('Основные данные')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="ФИО">{data.fullName?.trim() || EMPTY_FIELD}</Descriptions.Item>
              <Descriptions.Item label="Телефон">{data.phone?.trim() || <Text type="secondary">не указан</Text>}</Descriptions.Item>
              <Descriptions.Item label="Перевозчик">
                {data.carrier ? (
                  <Link to={`/carriers/${data.carrier.id}`}>{data.carrier.name}</Link>
                ) : (
                  <Text type="secondary">не указан</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title={sectionTitle('ТС (по умолчанию)')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Тягач">
                {truck ? (
                  <span>
                    <Link to={`/vehicles/${truck.id}`}>
                      <Text strong>{truck.registrationNumber}</Text>
                    </Link>
                    <Text type="secondary"> · {vehicleCategoryLabel(truck.vehicleCategory)}</Text>
                    {truck.brand ? <Text type="secondary"> ({truck.brand})</Text> : null}
                  </span>
                ) : (
                  <Text type="secondary">не указан</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Прицеп">
                {trailer ? (
                  <span>
                    <Link to={`/vehicles/${trailer.id}`}>
                      <Text strong>{trailer.registrationNumber}</Text>
                    </Link>
                    <Text type="secondary"> · {vehicleCategoryLabel(trailer.vehicleCategory)}</Text>
                    {trailer.brand ? <Text type="secondary"> ({trailer.brand})</Text> : null}
                  </span>
                ) : (
                  <Text type="secondary">не указан</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </div>

        <Card
          className="driver-card-routes"
          size="small"
          style={{ marginTop: '1.25rem', ...cardSurface }}
          styles={{ body: { padding: '12px 14px' } }}
        >
          <Tabs
            items={[
              {
                key: 'routes',
                label: 'Маршруты',
                children: routesError ? (
                  <Alert type="warning" message="Не удалось загрузить маршруты" showIcon />
                ) : routesLoading ? (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                  </div>
                ) : routes.length === 0 ? (
                  <Text type="secondary">{emptyListMessage('маршрутов с этим водителем')}</Text>
                ) : (
                  <Table<Route>
                    className="table-card-rows entity-card-table"
                    size="small"
                    rowKey="id"
                    columns={routeColumns}
                    dataSource={routes}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 15, showTotal: (t) => `Показано: ${t}` }}
                    onRow={(record) => ({
                      onClick: () => navigate(`/routes/${record.id}`),
                      style: { cursor: 'pointer' }
                    })}
                  />
                )
              },
              {
                key: 'applications',
                label: 'Заявки',
                children: routesError ? (
                  <Alert type="warning" message="Не удалось загрузить данные маршрутов" showIcon />
                ) : routesLoading ? (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                  </div>
                ) : uniqueApplications.length === 0 ? (
                  <Text type="secondary">{emptyListMessage('заявок по маршрутам этого водителя')}</Text>
                ) : (
                  <Table<Application>
                    className="table-card-rows entity-card-table"
                    size="small"
                    rowKey="id"
                    columns={applicationColumns}
                    dataSource={uniqueApplications}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 15, showTotal: (t) => `Показано: ${t}` }}
                    onRow={(record) => ({
                      onClick: () => navigate(`/applications/${record.id}`),
                      style: { cursor: 'pointer' }
                    })}
                  />
                )
              },
              {
                key: 'documents',
                label: 'Документы',
                children: (
                  <EntityDocumentsBlock
                    entityType="driver"
                    entityId={data.id}
                    canModify={canModifyDriver}
                    documentDetails={{
                      PASSPORT: { number: data.passportData },
                      DRIVER_LICENSE: { number: data.driverLicense }
                    }}
                  />
                )
              }
            ]}
          />
        </Card>
      </PageLayout>

      {canModifyDriver && (
        <CreateDriverModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          driver={data}
          onDriverCreated={() => {
            setEditOpen(false);
            void refetchDriver();
          }}
        />
      )}
    </div>
  );
};

const DriverCardPage: React.FC = () => (
  <EntityCardOverlayFrame>
    <DriverCardPageContent />
  </EntityCardOverlayFrame>
);

export default DriverCardPage;
